import PropTypes from 'prop-types';
import IconTile from './IconTile';
import SectionHeader from './SectionHeader';
import './primitives.css';

/**
 * SectionCard
 *
 * A container for one section of a page ("Today's lessons", "Recent grades").
 * Its head is the same 40px band `SectionHeader` draws standalone, so a section
 * reads identically whether or not it is in a card.
 *
 * Padding is 12px — `docs/design-language.md` §3 rule 6, which reserves 16px
 * for hero cards. `hero` opts into that, plus the wider radius (§2) and the
 * girih motif at 4% (§5, hero cards and empty states only).
 *
 *   - title       : main section label (string or node)
 *   - icon        : optional leading glyph, rendered as an IconTile
 *   - meta        : small count or range on the title's baseline
 *   - actions     : right-aligned controls (filters, "View all" links)
 *   - flush       : remove body padding for tables / lists that paint their own
 *   - hero        : the one card that is the point of the page
 *
 * A SectionCard nested inside another one loses its own surface (§3 rule 2 —
 * no card inside a card); it becomes a labelled block on the parent's ground.
 */
const SectionCard = ({
  title,
  icon,
  tone = 'neutral',
  subject,
  meta,
  actions,
  flush = false,
  hero = false,
  children,
  className = '',
  bodyClassName = '',
  bodyStyle,
}) => (
  <section
    className={[
      'lms-section-card',
      hero ? 'lms-section-card--hero lms-motif' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ')}
  >
    {(title || actions) && (
      <SectionHeader
        className="lms-section-card__head"
        title={title}
        icon={icon}
        tone={tone}
        subject={subject}
        meta={meta}
        actions={actions}
      />
    )}
    <div
      className={[
        'lms-section-card__body',
        flush ? 'lms-section-card__body--flush' : '',
        bodyClassName,
      ]
        .filter(Boolean)
        .join(' ')}
      style={bodyStyle}
    >
      {children}
    </div>
  </section>
);

SectionCard.propTypes = {
  title: PropTypes.node,
  icon: PropTypes.node,
  tone: IconTile.propTypes.tone,
  subject: IconTile.propTypes.subject,
  meta: PropTypes.node,
  actions: PropTypes.node,
  flush: PropTypes.bool,
  hero: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  bodyClassName: PropTypes.string,
  /** Only for CSS custom properties a layout must compute — never raw values. */
  bodyStyle: PropTypes.object,
};

export default SectionCard;
